import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTonConnectUI, useTonWallet, useTonAddress, useIsConnectionRestored } from '@tonconnect/ui-react';
import { Address } from '@ton/core';
import { track, markPlausibleLoadFailed } from './lib/track';
// CF-BRAND-LION-DIAMOND-V1: bundled diamond mark replaces platform 💎 emoji
// (device emoji fonts differ per OS; bundled art renders identically everywhere).
import cfDiamondMark from './assets/brand/cf-diamond.svg';
import { CoinFlip } from './components/CoinFlip';
import { History } from './components/History';
import { Verify } from './components/Verify';
import { Leaderboard } from './components/Leaderboard';
import { Feedback } from './components/Feedback';
import { HeaderMenu } from './components/HeaderMenu';
import { Onboarding } from './components/Onboarding';
import { WithdrawModal } from './components/WithdrawModal';
import { TermsPage } from './pages/Terms';
import { PrivacyPage } from './pages/Privacy';
import { BonusTermsPage } from './pages/BonusTerms';
import { BONUS_AVAILABLE_IN_BUILD } from './lib/bonus-availability';
import { BonusPanel } from './components/BonusPanel';
import { WalletMoneyCell } from './components/WalletMoneyCell';
import { useBonusState } from './lib/use-bonus-state';
import { useGrowthState } from './lib/use-growth-state';
import { WinsTicker } from './components/WinsTicker';
import { apiFetch } from './lib/api';
// CF-ANALYTICS-FOUNDATION-V1 Ph-2: per-event client beacon shell — app-shell lifecycle events
// (app_opened / session_started / session_ended) + install the app-wide visibility/pagehide flush.
import { recordEvent, flushEvents, installEventsBeacon, SESSION_ID } from './lib/events-beacon';
import { isBalanceFresh, shouldRetainOnFailure } from './lib/balance-freshness';
import { balanceSurface } from './lib/balance-surface';
import { parseTonAmount, formatTonExact } from './lib/ton-format';
import { shouldShowOnboarding } from './lib/onboarding';
import { useT } from './i18n';
import { isDragonTiger, isGerman, isBrazil, TELEGRAM_CHANNEL_URL, TELEGRAM_BOT_URL } from './lib/app-variant';

type Screen = 'flip' | 'history' | 'verify' | 'leaderboard' | 'feedback';

// Custodial balance state per R6 Binding 3 + CF-V11-CTA-MORPH-V1 R4 SIGN binding 9.
// The discriminated union forces every consumer to handle `loading` and `error`
// explicitly; there is no truthy/falsy shortcut that lets malformed balance data
// slip into the real-money UI surface (flip / deposit / withdraw / wallet-switch).
// tier_config added with fail-closed parser — no local constants fallback.
import type { TierConfigPayload, TierConfigEntry, BoostTier } from './lib/boost-tier-config';

export type BalanceState =
    | { kind: 'loading' }
    | { kind: 'error'; code: string }
    | {
        kind: 'loaded';
        balance_nano: string;
        max_bet_nano: string;
        nonce: number;
        wallet_address: string | null;
        tier_config: TierConfigPayload;
        // CF-REMOVE-REDSCREEN-WALLS-V1: when this snapshot was parsed + loaded.
        // Display may keep showing it across transport blips; real-money actions
        // gate on isBalanceFresh(loadedAtMs, now) so a stale snapshot can't act.
        loadedAtMs: number;
      };

const NANO_DECIMAL_RE = /^[0-9]+$/;
const VALID_TIER_KEYS: ReadonlyArray<BoostTier> = ['standard', 'boost', 'mega'];
const VALID_MULT_BPS = new Set([20000, 22000, 25000]);
const VALID_FEE_BPS = new Set([0, 1500, 3500]);

// Validate the /api/v1/balance DTO atomically. All five required fields
// (balance_nano, max_bet_nano, nonce, wallet_address, tier_config) must pass
// shape validation; any single failure returns the `error` state with a code
// describing which field rejected. Per R6 SIGN Binding 2: wallet_address must
// be either null or string. Per CF-V11-CTA-MORPH-V1 R4 SIGN binding 9: tier_config
// MUST be present + structurally valid (no silent fallback to local constants).
function parseTierConfig(raw: unknown): TierConfigPayload | null {
    if (typeof raw !== 'object' || raw === null) return null;
    const d = raw as Record<string, unknown>;
    if (!Array.isArray(d.tiers)) return null;
    const tiers: TierConfigEntry[] = [];
    for (const t of d.tiers) {
        if (typeof t !== 'object' || t === null) return null;
        const te = t as Record<string, unknown>;
        if (typeof te.key !== 'string' || !VALID_TIER_KEYS.includes(te.key as BoostTier)) return null;
        if (typeof te.label !== 'string' || te.label.length === 0) return null;
        if (typeof te.multiplier_bps !== 'number' || !Number.isInteger(te.multiplier_bps) || !VALID_MULT_BPS.has(te.multiplier_bps)) return null;
        if (typeof te.fee_bps !== 'number' || !Number.isInteger(te.fee_bps) || !VALID_FEE_BPS.has(te.fee_bps)) return null;
        tiers.push({
            key: te.key as BoostTier,
            label: te.label,
            multiplier_bps: te.multiplier_bps,
            fee_bps: te.fee_bps,
        });
    }
    // Must contain all 3 tier keys (Standard + Boost + Mega; backend always emits all).
    const keys = new Set(tiers.map((t) => t.key));
    if (keys.size !== 3 || !VALID_TIER_KEYS.every((k) => keys.has(k))) return null;
    return { tiers };
}

function parseBalanceDto(raw: unknown, nowMs: number): BalanceState {
    if (typeof raw !== 'object' || raw === null) {
        return { kind: 'error', code: 'invalid_balance_shape' };
    }
    const d = raw as Record<string, unknown>;
    const balance_nano = d.balance_nano;
    const max_bet_nano = d.max_bet_nano;
    const nonce = d.nonce;
    const wallet_address = d.wallet_address;
    if (typeof balance_nano !== 'string' || !NANO_DECIMAL_RE.test(balance_nano)) {
        return { kind: 'error', code: 'invalid_balance_shape' };
    }
    if (typeof max_bet_nano !== 'string' || !NANO_DECIMAL_RE.test(max_bet_nano)) {
        return { kind: 'error', code: 'invalid_balance_shape' };
    }
    if (typeof nonce !== 'number' || !Number.isInteger(nonce) || nonce < 0) {
        return { kind: 'error', code: 'invalid_balance_shape' };
    }
    if (wallet_address !== null && typeof wallet_address !== 'string') {
        return { kind: 'error', code: 'invalid_balance_shape' };
    }
    const tier_config = parseTierConfig(d.tier_config);
    if (tier_config === null) {
        // Fail-closed per CF-V11-CTA-MORPH-V1 R4 SIGN binding 9: missing or
        // malformed tier_config → error state. No silent Standard fallback.
        return { kind: 'error', code: 'invalid_tier_config' };
    }
    return { kind: 'loaded', balance_nano, max_bet_nano, nonce, wallet_address, tier_config, loadedAtMs: nowMs };
}

// In dev: proxy handles /api → localhost:3100
// In production: Cloudflare tunnel (api.cricketflip.com)
const API_BASE = import.meta.env.VITE_API_URL || (
    typeof window !== 'undefined' && window.location.hostname === 'localhost' ? '' : 'https://api.cricketflip.com'
);

// TON API for wallet balance — testnet when VITE_TON_NETWORK=testnet, mainnet otherwise.
const TON_NETWORK = import.meta.env.VITE_TON_NETWORK || 'mainnet';
const TON_API = TON_NETWORK === 'testnet'
    ? 'https://testnet.toncenter.com/api/v2'
    : 'https://toncenter.com/api/v2';

// CF-WITHDRAW-DISPLAY-PRECISION-V1: the local 2-decimal `formatNano` truncation was
// removed. Custodial balances (validated bigints) format via `formatTonExact`; the
// informational on-chain wallet balance (a nano string) goes through fail-loud
// `parseTonAmount` (canonical EXACT formatter — lib/ton-format.ts).

function shortenAddr(addr: string): string {
    if (!addr || addr.length < 10) return addr || '';
    return `${addr.slice(0, 4)}...${addr.slice(-4)}`;
}

function parseBigIntSafe(s: string | null | undefined): bigint | null {
    if (s == null) return null;
    try { return BigInt(s); } catch { return null; }
}

export default function App() {
    const t = useT();
    const [tonConnectUI] = useTonConnectUI();
    const wallet = useTonWallet();
    const userFriendlyAddress = useTonAddress();
    const rawAddress = useTonAddress(false);
    // CF-DEMO-BALANCE-FLASH-FIX-V1 (PL #7274): true once TonConnect finishes the
    // initial restore of a saved wallet. Until then CoinFlip renders a neutral
    // RESTORING surface (no demo balance / Practice / CTA / enabled flip) so a
    // returning real-money player never sees the fake "100 CREDITS" flash.
    // Restore-complete primitive, not a settle timer — Scorer design-align 1208Z.
    const connectionRestored = useIsConnectionRestored();
    const [screen, setScreen] = useState<Screen>('flip');
    const [stats, setStats] = useState<any>(null);
    const [balance, setBalance] = useState<string | null>(null);
    // BalanceState — single source of truth for custodial balance per R6
    // Binding 3. `loading` while the first fetch is in flight, `error` when
    // the service is unreachable OR the response shape is invalid, `loaded`
    // ONLY after all four required DTO fields validate. CoinFlip and the
    // App-level wallet menu / withdraw / switch-warning gates consume this
    // discriminated union; never the loose field tuple they used to read.
    const [balanceState, setBalanceState] = useState<BalanceState>({ kind: 'loading' });
    // CF-REMOVE-REDSCREEN-WALLS-V1: clock driving the action-freshness gate. A
    // ticker advances it while the app is foreground; on resume from phone-lock /
    // app-switch we snap it forward so a snapshot that went stale while hidden is
    // detected immediately (before the re-probe lands), keeping flip/withdraw
    // fail-closed. Display is never gated on this — only money actions are.
    const [nowMs, setNowMs] = useState<number>(() => Date.now());
    // Derived legacy views — kept for the existing CoinFlip props and the
    // body of this component. They are projections of the discriminated
    // union above; they DO NOT carry meaning when balanceState.kind !==
    // 'loaded'. Use balanceState directly when gating actions on money state.
    const depositBalance = balanceState.kind === 'loaded' ? balanceState.balance_nano : null;
    const maxBetNano = balanceState.kind === 'loaded' ? balanceState.max_bet_nano : null;
    const balanceLoaded = balanceState.kind === 'loaded';
    // CF-REMOVE-REDSCREEN-WALLS-V1: freshness projection. `balanceFresh` gates the
    // real-money action surfaces (flip in CoinFlip, withdraw-open here); display is
    // NOT gated on it — a loaded balance keeps showing across transport blips.
    const balanceLoadedAtMs = balanceState.kind === 'loaded' ? balanceState.loadedAtMs : null;
    const balanceFresh = isBalanceFresh(balanceLoadedAtMs, nowMs);
    // CF-WD-CROSS-DEVICE-DEST-V1 (incident CF-WD-HOTWALLET-SELFSEND-260610): the withdrawal
    // destination is ALWAYS the account's BOUND wallet from the server balance snapshot —
    // NEVER the ambient TON-Connect wallet (rawAddress/userFriendlyAddress), which is
    // per-device state: the house wallet connected on another device is how a withdrawal
    // self-sent to the house. Normalized to the friendly non-bounceable form for the wire
    // + display. No bound wallet (or an unparseable one) -> null -> the withdraw modal
    // cannot open; the backend dest==bound guard fails closed on its side regardless.
    const boundWalletAddress = useMemo(() => {
        if (balanceState.kind !== 'loaded' || !balanceState.wallet_address) return null;
        try {
            return Address.parse(balanceState.wallet_address).toString({ urlSafe: true, bounceable: false });
        } catch {
            return null;
        }
    }, [balanceState]);
    const [verifyFlipId, setVerifyFlipId] = useState<string | null>(null);
    // CF-V11-CTA-MORPH-V1: selectedTier owned at App-level so it persists across
    // CoinFlip-internal remounts (deposit/withdraw modal close → CoinFlip
    // re-renders but selection survives). Default 'standard' on cold load —
    // never auto-promote to a fee-bearing tier. PL #5875 "genius" design.
    const [selectedTier, setSelectedTier] = useState<BoostTier>('standard');
    const [showOnboarding, setShowOnboarding] = useState(() => {
        // CF-ONBOARDING-PREVIEW-V1 (PL #7696/#7698) + CF-FUNNEL-INSTANT-PLAY-V1 (PL #9263):
        // new users land DIRECTLY on the playable game (demo FLIP ready) — the 3-slide intro
        // is no longer an auto-gate (it tanked ad-acquired activation). Only Telegram
        // startapp=fresh replays the intro (owner preview); the ⋯ "How it works" menu item
        // re-shows it on demand. Error path also lands on the game (consistent + demo-safe).
        try {
            const sp: string | undefined = (window as any).Telegram?.WebApp?.initDataUnsafe?.start_param;
            return shouldShowOnboarding(sp);
        } catch { return false; }
    });
    // CF-TERMS-GATE-REMOVE-V1 (PL #7106-7124): removed the tosAccepted state — the
    // "Before you play" TOS/age-gate popup is gone (friction; not required by Telegram).
    // Terms stay reachable via the small Flip-screen footer link → /terms.

    // Reset to flip if the wallet disconnects while on the Verify tab. The
    // tab is hidden for disconnected sessions and the verify endpoint requires
    // an authenticated request.
    const prevWalletRef = useRef(wallet);
    useEffect(() => {
        if (prevWalletRef.current && !wallet && screen === 'verify') {
            setScreen('flip');
            setVerifyFlipId(null);
        }
        prevWalletRef.current = wallet;
    }, [wallet, screen]);

    useEffect(() => {
        const win = window as unknown as { Telegram?: { WebApp?: { initDataUnsafe?: { user?: unknown } } }; __cfPlausibleLoadFailed?: string };
        const fromTelegram = typeof window !== 'undefined' && win.Telegram?.WebApp?.initDataUnsafe?.user != null;
        track.appOpen(fromTelegram ? 'telegram' : 'direct');
        const timer = window.setTimeout(() => {
            if (win.__cfPlausibleLoadFailed) markPlausibleLoadFailed(win.__cfPlausibleLoadFailed);
        }, 3000);
        return () => window.clearTimeout(timer);
    }, []);

    // CF-JINLONGHU-ENTRY-TRACKING-V1: one-time Mini App entry beacon. Fires only inside Telegram
    // (initData present) so the browser gate never calls it; best-effort (.catch) — never blocks UI.
    // Backend /api/v1/entry records first-touch source (signed start_param) + pages CF Ops once.
    useEffect(() => {
        if ((window as any).Telegram?.WebApp?.initData) {
            void apiFetch(`${API_BASE}/api/v1/entry`, { method: 'POST' }).catch((err) => {
                console.warn('entry beacon failed', err);
            });
        }
    }, []);

    // CF-ANALYTICS-FOUNDATION-V1 Ph-2: app-shell lifecycle events (app_opened / session_started /
    // session_ended) through the per-event beacon. Gated on initData (same as the entry beacon) so
    // the unauthed browser gate never emits (the /events route is auth-fail-closed → a 401). The
    // beacon body is identity-free; the server maps the signed initData to an opaque subject_id.
    // Telegram launch params (start_param, platform, locale) are NON-identity context; is_first_open
    // is a localStorage flag — NO tg_user_id / username / wallet is ever read here.
    useEffect(() => {
        const wa = (window as any).Telegram?.WebApp;
        if (!wa?.initData) return; // unauthed browser gate → no beacon (route would 401)

        installEventsBeacon(API_BASE); // capture apiBase + wire the app-wide visibility/pagehide flush

        // is_first_open: best-effort localStorage flag (NOT identity). Defaults to true if storage is
        // unavailable (private mode) — a conservative over-count beats reading any identity.
        let isFirstOpen = true;
        try {
            isFirstOpen = localStorage.getItem('cf_first_open_seen') === null;
            if (isFirstOpen) localStorage.setItem('cf_first_open_seen', '1');
        } catch { /* storage blocked → treat as first open */ }

        const launch = wa.initDataUnsafe ?? {};
        const startParam: unknown = launch.start_param;
        const tgPlatform: unknown = wa.platform;
        // locale: prefer the signed Telegram user language_code is identity-adjacent → use the
        // non-identity WebApp.initDataUnsafe? NO — language_code lives on the user object (identity).
        // Read the browser navigator.language instead (non-identity UI locale) so no PII is touched.
        let locale: string | null = null;
        try { locale = typeof navigator !== 'undefined' && navigator.language ? navigator.language : null; } catch { locale = null; }

        recordEvent({
            event_type: 'app_opened',
            session_id: SESSION_ID,
            properties: {
                start_param: typeof startParam === 'string' ? startParam : null,
                tg_platform: typeof tgPlatform === 'string' ? tgPlatform : null,
                locale,
                is_first_open: isFirstOpen,
            },
        });
        recordEvent({
            event_type: 'session_started',
            session_id: SESSION_ID,
            properties: {},
        });

        const sessionStartMs = Date.now();
        let sessionEndedSent = false;
        const sendSessionEnded = (endReason: string) => {
            if (sessionEndedSent) return; // one session_ended per app-open (dedup_key has none → guard here)
            sessionEndedSent = true;
            recordEvent({
                event_type: 'session_ended',
                session_id: SESSION_ID,
                properties: {
                    duration_secs: Math.max(0, Math.round((Date.now() - sessionStartMs) / 1000)),
                    end_reason: endReason,
                },
            });
            flushEvents(); // best-effort flush on the way out (the shell also flushes on hide)
        };
        const onHide = () => {
            if (typeof document !== 'undefined' && document.visibilityState !== 'hidden') return;
            sendSessionEnded('hidden');
        };
        const onPageHide = () => sendSessionEnded('pagehide');
        document.addEventListener('visibilitychange', onHide);
        window.addEventListener('pagehide', onPageHide);
        return () => {
            document.removeEventListener('visibilitychange', onHide);
            window.removeEventListener('pagehide', onPageHide);
            sendSessionEnded('unmount');
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const prevWalletAddressForTrackingRef = useRef<string | undefined>(userFriendlyAddress);
    useEffect(() => {
        const prev = prevWalletAddressForTrackingRef.current;
        const curr = userFriendlyAddress;
        if (!prev && curr) {
            track.walletConnectSuccess('tonconnect');
        }
        // CF-FRONTEND-WITHDRAW-STATE-LATCH-FIX-V1: clear withdraw descriptor on
        // any wallet address change (connect / disconnect / switch). A withdraw
        // captured for a previous bound wallet must not survive a wallet swap —
        // the destination address would be wrong.
        if (prev !== curr) {
            setWithdrawDescriptor(null);
            // CF-REMOVE-REDSCREEN-WALLS-V1 constraint 6: drop any retained loaded
            // balance on wallet connect / switch / disconnect so a previous wallet's
            // balance cannot survive into the new binding or a withdraw descriptor.
            // The poll effect re-probes immediately because fetchBalance's identity
            // changes with userFriendlyAddress.
            setBalanceState({ kind: 'loading' });
        }
        prevWalletAddressForTrackingRef.current = curr;
    }, [userFriendlyAddress]);

    const [showWalletMenu, setShowWalletMenu] = useState(false);
    // CF-FRONTEND-WITHDRAW-STATE-LATCH-FIX-V1 (2026-05-27): replace the bare
    // `showWithdraw: boolean` with an explicit descriptor snapshot. The prior
    // model coupled the modal's open-intent to live balance via render guard
    // (`showWithdraw && hasKnownPositiveBalance && balNano !== null`). When
    // background balance polling hit an error/transient (CoinFlip "Service
    // data unavailable" screen), `hasKnownPositiveBalance` became false and
    // the modal unmounted via render guard — but `showWithdraw` stayed `true`
    // because nothing reset it. On the next deposit, balance recovered to
    // positive and the modal silently remounted, presenting as auto-open.
    // The descriptor captures balance+address AT THE OPEN INSTANT (deliberate
    // action only) and is cleared only on close/success-terminal/wallet-change.
    const [withdrawDescriptor, setWithdrawDescriptor] = useState<{ balanceNano: string; address: string } | null>(null);
    const [showSwitchWarning, setShowSwitchWarning] = useState(false);
    const [pendingWalletAction, setPendingWalletAction] = useState<'switch' | 'disconnect' | null>(null);
    const [showDeposit, setShowDeposit] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    // CF-REMOVE-REDSCREEN-WALLS-V1 (PL #7138): the backendDown state, the
    // /api/health poll, and the "Service temporarily unavailable" wall were
    // removed. Backend liveness is now expressed through the balance poll's
    // classify-and-retain policy (see fetchDepositBalance) — transient transport
    // failures retain the last loaded balance and gate actions on freshness; they
    // never show a full-screen red wall.

    useEffect(() => {
        if (window.Telegram?.WebApp) {
            const wa = window.Telegram.WebApp;
            wa.ready();
            wa.expand();
            // CF-V11-UI-DARK-UNIFY-V1: unified dark theme per PL TG #6131
            // 2026-05-19. Telegram header/background match the deep dark page
            // background so the whole viewport reads as a single dark
            // Telegram app, not a frame wrapping a dark game card.
            wa.setHeaderColor('#070b14');
            wa.setBackgroundColor('#070b14');
        }
    }, []);

    // Telegram BackButton controller — closes topmost overlay, then nav-back to 'flip'.
    useEffect(() => {
        const wa = window.Telegram?.WebApp;
        if (!wa || !wa.BackButton) return;

        const backBtn = wa.BackButton;

        const onBackClick = () => {
            // CF-FUNNEL-INSTANT-PLAY-V1: the intro can now open mid-session from the ⋯
            // "How it works" menu (no longer only an app-init gate), so Back must dismiss
            // it — it's the topmost overlay when shown.
            if (showOnboarding) { setShowOnboarding(false); return; }
            if (showSwitchWarning) {
                setShowSwitchWarning(false);
                setPendingWalletAction(null);
                return;
            }
            if (showDeposit) { setShowDeposit(false); return; }
            if (showInfo) { setShowInfo(false); return; }
            if (withdrawDescriptor) { setWithdrawDescriptor(null); return; }
            if (showWalletMenu) { setShowWalletMenu(false); return; }
            if (screen !== 'flip') { setScreen('flip'); return; }
            backBtn.hide();
        };

        backBtn.onClick(onBackClick);

        const shouldShow = showOnboarding || showSwitchWarning || showDeposit || showInfo || !!withdrawDescriptor || showWalletMenu || screen !== 'flip';
        if (shouldShow) backBtn.show(); else backBtn.hide();

        return () => { backBtn.offClick(onBackClick); };
    }, [showOnboarding, screen, showDeposit, showInfo, withdrawDescriptor, showWalletMenu, showSwitchWarning]);

    const performWalletSwitch = async () => {
        try {
            if (tonConnectUI.connected) {
                await tonConnectUI.disconnect();
            }
            track.walletConnectStart('tonconnect');
            await tonConnectUI.openModal();
        } catch (err) {
            console.error('🚨 wallet switch failed', err);
            track.walletConnectFail('tonconnect', err instanceof Error ? err.message : String(err));
        }
    };

    const handleConnectClick = async () => {
        track.walletConnectStart('tonconnect');
        try {
            await tonConnectUI.openModal();
        } catch (err) {
            track.walletConnectFail('tonconnect', err instanceof Error ? err.message : String(err));
        }
    };

    // Open the public /terms page in Telegram's in-app browser, with a
    // window.open fallback for non-Telegram contexts. Shared by the Flip-screen
    // footer link and the header ⋯ menu — both paths open Terms (never a silent
    // no-op). CF-HEADER-HELP-MENU-V1 DRYs the previously-inlined footer handler.
    const openTerms = () => {
        const url = `${window.location.origin}/terms`;
        const wa = (window as any).Telegram?.WebApp;
        if (wa && typeof wa.openLink === 'function') wa.openLink(url);
        else window.open(url, '_blank', 'noopener');
    };

    // Open the public /privacy page in Telegram's in-app browser, with a
    // window.open fallback for non-Telegram contexts. Reached from the header
    // ⋯ menu (never a silent no-op). CF-PRIVACY-PAGE-V1. This is the URL
    // registered in @BotFather → Bot Settings → Privacy Policy.
    const openPrivacy = () => {
        const url = `${window.location.origin}/privacy`;
        const wa = (window as any).Telegram?.WebApp;
        if (wa && typeof wa.openLink === 'function') wa.openLink(url);
        else window.open(url, '_blank', 'noopener');
    };

    const performWalletDisconnect = async () => {
        try {
            if (tonConnectUI.connected) {
                await tonConnectUI.disconnect();
            }
        } catch (err) {
            console.error('🚨 wallet disconnect failed', err);
        }
    };

    // Wallet-switch warning fires when the custodial balance is non-zero AND
    // known, OR when the load hasn't completed yet (we can't be sure). The
    // predicates use the custodial loaded-balance truth (Binding 8), not any
    // legacy channel-status concept.
    const isBalanceUnsafe = (): boolean => {
        const balNano = parseBigIntSafe(depositBalance);
        const hasFunds = balNano !== null && balNano > 0n;
        const statusUnknown = !!wallet && !balanceLoaded;
        return hasFunds || statusUnknown;
    };

    const handleSwitchClick = () => {
        setShowWalletMenu(false);
        if (isBalanceUnsafe()) {
            setPendingWalletAction('switch');
            setShowSwitchWarning(true);
        } else {
            void performWalletSwitch();
        }
    };

    const handleDisconnectClick = () => {
        setShowWalletMenu(false);
        if (isBalanceUnsafe()) {
            setPendingWalletAction('disconnect');
            setShowSwitchWarning(true);
        } else {
            void performWalletDisconnect();
        }
    };

    const handleWarningContinue = () => {
        const action = pendingWalletAction;
        setShowSwitchWarning(false);
        setPendingWalletAction(null);
        if (action === 'switch') void performWalletSwitch();
        else if (action === 'disconnect') void performWalletDisconnect();
    };

    const handleWarningWithdrawFirst = () => {
        setShowSwitchWarning(false);
        setPendingWalletAction(null);
        // Snapshot balance + address at deliberate-open instant. If either
        // is missing we noop rather than open an empty modal — the warning's
        // own predicate already gated this branch on a positive balance.
        const balNanoSnap = parseBigIntSafe(depositBalance);
        // CF-WD-CROSS-DEVICE-DEST-V1: dest = the BOUND wallet, never the connected one.
        const addrSnap = boundWalletAddress;
        // CF-REMOVE-REDSCREEN-WALLS-V1: withdraw only opens on a bounded-fresh balance.
        if (balNanoSnap !== null && balNanoSnap > 0n && addrSnap && balanceFresh) {
            setWithdrawDescriptor({ balanceNano: balNanoSnap.toString(), address: addrSnap });
        }
    };

    const handleWarningCancel = () => {
        setShowSwitchWarning(false);
        setPendingWalletAction(null);
    };

    useEffect(() => {
        apiFetch(`${API_BASE}/api/stats`)
            .then(r => r.json())
            .then(setStats)
            .catch((err) => {
                console.error('🚨 /api/stats fetch failed', err);
            });
    }, []);

    // Fetch wallet's on-chain TON balance (informational only).
    // CF-BONUS Ticket B: THE single owner of bonus state (Scorer R3). Every consumer reads
    // from here; nothing else fetches. Refreshed on mount + the five state-changing events.
    // ⛔ Session readiness is passed IN: the coordinator issues zero physical `/bonus` reads before
    // it, which is what stops the old mount read racing auth and 401-ing on every cold open.
    const { bonusView, bonusBootstrapped, bonusInitialReadExhausted, refreshBonus } =
        useBonusState(API_BASE, balanceState.kind === 'loaded');
    // CF-GROWTH-V1: de-identified wins ticker (fetch-owner hook; mounts self-fetch).
    // CF-POT-COUNTER-REMOVE-V1: the pot counter this hook also served is gone (PL TG #12916).
    const { ticker, refreshGrowth } = useGrowthState(API_BASE);
    // CF-BONUS-FLASH-FIX-V1: the mount read races session auth and 401s; re-read on the edge
    // where the session first becomes usable. Edge-triggered (kind transition into 'loaded'),
    // never a poll — the effect body only fires when `loaded` flips false→true.
    // ⛔ The explicit `session_ready` refresh is GONE, not disabled. Session readiness is now an
    // input to `useBonusState` above, and the coordinator starts its own establishment the moment
    // the session becomes usable. Keeping this effect as well would spend a second `/bonus` token
    // for a read the owner is already making.

    const fetchBalance = useCallback(async () => {
        if (!userFriendlyAddress) { setBalance(null); return; }
        try {
            const tonResp = await fetch(`${TON_API}/getAddressBalance?address=${userFriendlyAddress}`);
            const tonData = await tonResp.json();
            if (tonData.ok && tonData.result) {
                setBalance(tonData.result);
            }
        } catch {
            setBalance(null);
        }
    }, [userFriendlyAddress]);

    // Fetch custodial balance from the backend. tg_user_id is derived server-side
    // from the verified init-data header; no client-supplied user identity is
    // accepted. Atomic shape validation per R6 Binding 3 — `parseBalanceDto`
    // either yields a fully-validated `loaded` state or an `error` state with
    // a code; never a partial mixture. Network/HTTP errors map to discrete
    // error codes so the UI can render a non-actionable retry surface
    // instead of falling open into demo/deposit-CTA mode.
    const fetchDepositBalance = useCallback(async () => {
        try {
            const resp = await apiFetch(`${API_BASE}/api/v1/balance`);
            if (!resp.ok) {
                // CF-REMOVE-REDSCREEN-WALLS-V1: classify the failure. Transient
                // gateway statuses (502/503/504) are transport-class — keep the last
                // loaded balance on screen (no clobber) and let the freshness gate
                // withhold actions if it goes stale. Everything else (auth/4xx, 500)
                // clears to the neutral state.
                if (shouldRetainOnFailure({ kind: 'http', status: resp.status })) {
                    console.warn('⚠️ /api/v1/balance transient HTTP', resp.status, '— retaining last loaded balance');
                    return;
                }
                console.error('🚨 /api/v1/balance non-retainable HTTP — clearing to neutral', resp.status);
                setBalanceState({ kind: 'error', code: `http_${resp.status}` });
                return;
            }
            let data: unknown;
            try {
                data = await resp.json();
            } catch (err) {
                // Malformed JSON is schema-class — never retain (don't bless garbage).
                console.error('🚨 /api/v1/balance JSON parse failed — clearing to neutral', err);
                setBalanceState({ kind: 'error', code: 'json_parse_failed' });
                return;
            }
            // parseBalanceDto yields a fully-validated loaded snapshot (stamped with
            // loadedAtMs) OR an error (invalid shape / tier_config) — schema-class,
            // which clears. Stamp loadedAtMs at parse time.
            setBalanceState(parseBalanceDto(data, Date.now()));
        } catch (err) {
            // fetch threw / aborted on timeout = transport-class. Consult the policy
            // (network ⇒ retain) and keep the last loaded balance; the fail-closed
            // branch clears only if the policy ever reclassifies network failures.
            if (shouldRetainOnFailure({ kind: 'network' })) {
                console.warn('⚠️ /api/v1/balance network failure — retaining last loaded balance', err);
                return;
            }
            console.error('🚨 /api/v1/balance fetch failed — clearing to neutral', err);
            setBalanceState({ kind: 'error', code: 'network_error' });
        }
    }, []);

    useEffect(() => {
        const probe = () => { fetchBalance(); fetchDepositBalance(); };
        probe(); // immediate on mount + on wallet-address change (fetchBalance identity)
        // Poll the custodial balance every 15s while the app is FOREGROUND. Skip
        // polling while document.hidden (phone lock / app-switch): there's no UI to
        // update and the freshness gate already withholds stale actions. Re-probe
        // immediately on foreground resume so a stale snapshot revalidates.
        const interval = setInterval(() => {
            if (document.hidden) return;
            probe();
        }, 15000);
        const onVisible = () => { if (document.visibilityState === 'visible') probe(); };
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [fetchBalance, fetchDepositBalance]);

    // CF-REMOVE-REDSCREEN-WALLS-V1: advance the freshness clock. A 5s ticker keeps
    // the action-freshness gate reactive while visible (transport failures retain
    // the balance WITHOUT a state write, so without this the Flip/Withdraw controls
    // would not disable as the snapshot ages). On foreground resume we snap nowMs
    // forward so a snapshot that went stale during a phone-lock is detected
    // immediately; the poll re-probe re-enables actions once a fresh DTO lands.
    useEffect(() => {
        const id = setInterval(() => setNowMs(Date.now()), 5000);
        const onVisible = () => { if (document.visibilityState === 'visible') setNowMs(Date.now()); };
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            clearInterval(id);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, []);

    // ═══ /terms route (public + reachable in-app via the Flip-screen Terms·Privacy footer) ═══
    // CF-TERMS-GATE-REMOVE-V1 (PL #7106-7124): the "Before you play" TOS/age-gate popup was
    // removed (friction; Telegram does not require it). Terms stay reachable at /terms.
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
    if (currentPath === '/terms') {
        return <TermsPage />;
    }
    // ═══ /privacy route (public; the URL registered in @BotFather → Bot Settings → Privacy Policy) ═══
    if (currentPath === '/privacy') {
        return <PrivacyPage />;
    }
    // ═══ /bonus-terms route (CF-BONUS Ticket B) ═══
    // Public and linked from the deposit teaser + the active bonus card. Sits ABOVE the beta
    // gate deliberately, exactly like /terms and /privacy: a player must be able to read the
    // terms of an offer without first clearing an access gate.
    // Not registered at all on a non-participating build: the URL must not resolve, so the
    // terms of an offer that is not made cannot be reached even by typing the path.
    if (BONUS_AVAILABLE_IN_BUILD && currentPath === '/bonus-terms') {
        return <BonusTermsPage />;
    }

    // ═══ PRIVATE-BETA ACCESS GATE ═══
    const betaIdsEnv = (import.meta.env.VITE_BETA_USER_IDS || '').trim();
    if (betaIdsEnv) {
        const allowed = new Set(betaIdsEnv.split(/[\s,]+/).filter(Boolean));
        let currentUserId: string | null = null;
        try {
            const initData = (window as any).Telegram?.WebApp?.initData;
            if (initData) {
                const params = new URLSearchParams(initData);
                const userStr = params.get('user');
                if (userStr) currentUserId = String(JSON.parse(userStr).id);
            }
        } catch { /* fall through to not-allowed */ }

        if (!currentUserId || !allowed.has(currentUserId)) {
            return (
                <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center" style={{ background: 'var(--bg-deep)' }}>
                    <div className="text-6xl mb-6">🪙</div>
                    <h1 className="text-3xl font-bold text-white mb-3">{t('app.brand_name')}</h1>
                    {/* CF-GERMAN-MUENZO-REBRAND-V1: „Kopf oder Zahl" tagline paired
                        with the Münzo brand (german variant only — default/dragontiger
                        render byte-identical to HEAD). */}
                    {isGerman && (
                        <p className="text-sm text-slate-400 font-medium tracking-wide mb-2">Kopf oder Zahl</p>
                    )}
                    {/* CF-BRAZIL-VARIANT-V1: „Cara ou Coroa" tagline paired with the
                        MoedaFlip brand (brazil variant only — others byte-identical). */}
                    {isBrazil && (
                        <p className="text-sm text-slate-400 font-medium tracking-wide mb-2">Cara ou Coroa</p>
                    )}
                    <p className="text-lg text-yellow-500 font-semibold mb-2">{t('app.beta.badge')}</p>
                    <p className="text-slate-400 mb-8 max-w-sm">
                        {t('app.beta.tagline')}<br />
                        {t('app.beta.invite_only')}
                    </p>
                    <a
                        href={TELEGRAM_CHANNEL_URL}
                        className="inline-flex items-center gap-2 px-8 py-4 bg-[#2AABEE] hover:bg-[#229ED9] text-white font-bold rounded-2xl text-lg transition-colors shadow-lg"
                    >
                        {t('app.beta.notify_btn')}
                    </a>
                    <p className="text-slate-600 text-xs mt-6">
                        {t('app.beta.follow_note')}
                    </p>
                </div>
            );
        }
    }

    // Browser gate — only allow access from Telegram Mini App. Copy is custodial-
    // truthful: no on-chain anchoring claims, no smart-contract framing.
    const isTelegram = !!(window as any).Telegram?.WebApp?.initData;
    if (!isTelegram) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center" style={{ background: 'var(--bg-deep)' }}>
                <div className="text-6xl mb-6">🪙</div>
                <h1 className="text-3xl font-bold text-white mb-3">{t('app.brand_name')}</h1>
                {/* CF-GERMAN-MUENZO-REBRAND-V1: „Kopf oder Zahl" tagline paired with
                    the Münzo brand (german variant only — default/dragontiger render
                    byte-identical to HEAD). */}
                {isGerman && (
                    <p className="text-sm text-slate-400 font-medium tracking-wide mb-2">Kopf oder Zahl</p>
                )}
                {/* CF-BRAZIL-VARIANT-V1: „Cara ou Coroa" tagline paired with the
                    MoedaFlip brand (brazil variant only — others byte-identical). */}
                {isBrazil && (
                    <p className="text-sm text-slate-400 font-medium tracking-wide mb-2">Cara ou Coroa</p>
                )}
                <p className="text-lg text-yellow-500 font-semibold mb-2">{t('app.browser_gate.tagline')}</p>
                <p className="text-slate-400 mb-8 max-w-sm">
                    {t('app.browser_gate.subtagline')}
                </p>
                <a
                    href={TELEGRAM_BOT_URL}
                    className="inline-flex items-center gap-2 px-8 py-4 bg-[#2AABEE] hover:bg-[#229ED9] text-white font-bold rounded-2xl text-lg transition-colors shadow-lg"
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.19-.04-.27-.02-.12.03-1.99 1.27-5.62 3.72-.53.36-1.01.54-1.44.53-.47-.01-1.38-.27-2.06-.49-.83-.27-1.49-.42-1.43-.88.03-.24.37-.49 1.02-.74 3.99-1.74 6.65-2.89 7.99-3.44 3.8-1.58 4.59-1.86 5.1-1.87.11 0 .37.03.54.17.14.12.18.28.2.45-.01.06.01.24 0 .38z"/></svg>
                    {t('app.browser_gate.play_btn')}
                </a>
                <p className="text-slate-600 text-xs mt-6">
                    {t('app.browser_gate.exclusive_note')}
                </p>
            </div>
        );
    }

    if (showOnboarding) {
        return <Onboarding onComplete={() => setShowOnboarding(false)} />;
    }

    // CF-REMOVE-REDSCREEN-WALLS-V1 (PL #7138): the "Service temporarily unavailable"
    // backend-down wall was removed. Transient backend unreachability now retains
    // the last loaded balance (or holds the neutral skeleton if none ever loaded)
    // and gates actions on freshness — never a full-screen red wall.

    const balNano = parseBigIntSafe(depositBalance);
    const hasKnownPositiveBalance = balanceLoaded && balNano !== null && balNano > 0n;
    // CF-216 §6 wallet-menu repair. `hasUnknownPositiveBalance` used to be declared
    // here and was UNSATISFIABLE: it required `!balanceLoaded && balNano !== null`,
    // but `depositBalance` (:184) is null unless `balanceState.kind === 'loaded'`, so
    // `balNano` is null exactly when `!balanceLoaded`. The establishing row it gated
    // had therefore been unreachable for ~4 months. Asking the visibility authority is
    // the repair; restating the predicate under a new name would not be.
    // ⛔ Only `walletMenuCashStatus` is consumed here. This does NOT wire the chips
    // surface: the active bonus renders in <BonusPanel/> below, which is a sibling of
    // <CoinFlip/> and reads no balance state at all.
    const balanceSurfaceView = balanceSurface({
        balanceState,
        bonusView,
        bonusBootstrapped,
        bonusInitialReadExhausted,
        isConnected: !!wallet && !!userFriendlyAddress,
        connectionRestored,
    });
    // CF-V11-CTA-MORPH-V1: tier_config projection from the `loaded` arm.
    // Fail-closed — never coerce to a local-constant fallback for the
    // real-money tier UI. Demo / loading / error paths receive null and the
    // BoostTierSelector / morphing CTA are gated accordingly inside CoinFlip.
    const tierConfig: TierConfigPayload | null = balanceState.kind === 'loaded' ? balanceState.tier_config : null;

    return (
        <div className="min-h-screen min-h-[100dvh] flex flex-col" style={{ background: 'var(--page-bg)' }}>
            {/* Header — dark-theme text colors per CF-V11-UI-DARK-UNIFY-V1.
                The "Provably fair · Verifiable" subtitle is the trust anchor;
                tiered fees only activate on explicit player opt-in via
                BoostTierSelector below, where the morphing FLIP button shows
                exact cost. */}
            <header className="px-5 pt-3 pb-2">
                <div className="flex items-center justify-between">
                    {/* CF-DRAGONTIGER-THEME-V1 Phase B: brand lockup. Under the
                        dragontiger variant ONLY, swap the icon + "CricketFlip" +
                        subtitle for the 金龙虎 gold wordmark + GOLDEN DRAGON TIGER
                        subline + 可验证公平 trust badge (D2 + D5). The default app
                        renders the byte-identical original lockup. */}
                    {isDragonTiger ? (
                        <div className="dt-wordmark">
                            <div className="dt-wordmark-zh">金龙虎</div>
                            <div className="dt-wordmark-en">GOLDEN DRAGON TIGER</div>
                            <div className="dt-fair-badge mt-1.5">
                                <span aria-hidden="true">⚖</span>
                                <b>可验证公平</b>
                                <span>· Provably Fair</span>
                            </div>
                        </div>
                    ) : isGerman ? (
                        /* CF-GERMAN-MUENZO-REBRAND-V1: „Münzo" gold wordmark with
                           „Kopf oder Zahl" kept as the subtitle/tagline + „Nachweisbar
                           fair" trust badge. The badge wording applies ONLY to
                           flip/receipt verification. */
                        <div className="de-wordmark">
                            <div className="de-wordmark-main">Münzo</div>
                            <div className="de-wordmark-sub">Kopf oder Zahl</div>
                            <div className="de-fair-badge mt-1.5">
                                <span aria-hidden="true">⚖</span>
                                <b>Nachweisbar fair</b>
                                <span>· Überprüfbar</span>
                            </div>
                        </div>
                    ) : isBrazil ? (
                        /* CF-BRAZIL-VARIANT-V1: „MoedaFlip" gold wordmark with „Cara ou
                           Coroa" as the subtitle/tagline + „Comprovadamente justo" trust
                           badge. The badge wording applies ONLY to flip/receipt verification. */
                        <div className="br-wordmark">
                            <div className="br-wordmark-main">MoedaFlip</div>
                            <div className="br-wordmark-sub">Cara ou Coroa</div>
                            <div className="br-fair-badge mt-1.5">
                                <span aria-hidden="true">⚖</span>
                                <b>Comprovadamente justo</b>
                                <span>· Verificável</span>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2.5">
                            <div className="w-9 h-9 rounded-xl overflow-hidden flex items-center justify-center">
                                <img src="/icon.png" alt="CricketFlip" width={36} height={36} className="w-full h-full object-cover" />
                            </div>
                            <div>
                                <h1 className="text-[15px] font-bold tracking-tight" style={{ color: 'var(--page-text-primary)' }}>
                                    {t('app.brand_name')}
                                </h1>
                                <p className="text-[10px] font-medium tracking-widest uppercase" style={{ color: 'var(--page-text-secondary)' }}>
                                    {t('app.header.subtitle')}
                                </p>
                            </div>
                        </div>
                    )}
                    <div className="flex items-center gap-2">
                        {/* CF-DRAGONTIGER-THEME-V1 Phase B (D3): decorative 888 ribbon
                            accent — variant-gated, never near any money value. */}
                        {isDragonTiger && (
                            <span className="dt-ribbon" aria-hidden="true">888</span>
                        )}
                        {/* CF-GERMAN-VARIANT-V1: decorative hairline accent —
                            variant-gated, never near any money value. */}
                        {isGerman && (
                            <span className="de-hairline" aria-hidden="true">TON</span>
                        )}
                        {/* CF-BRAZIL-VARIANT-V1: decorative hairline accent —
                            variant-gated, never near any money value. */}
                        {isBrazil && (
                            <span className="br-hairline" aria-hidden="true">TON</span>
                        )}
                        {wallet ? (
                            <button
                                onClick={() => setShowWalletMenu(prev => !prev)}
                                className="flex items-center gap-2 px-3 py-1.5 rounded-full transition-all active:scale-95"
                                style={{ background: 'rgba(16, 185, 129, 0.14)', border: '1px solid rgba(16, 185, 129, 0.3)' }}
                            >
                                <span className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
                                      style={{ background: 'var(--emerald)' }} />
                                <span className="text-[11px] font-mono font-medium" style={{ color: 'var(--emerald)' }}>
                                    {shortenAddr(userFriendlyAddress)}
                                </span>
                            </button>
                        ) : (
                            <button
                                onClick={handleConnectClick}
                                className="px-4 py-1.5 rounded-full text-[11px] font-semibold transition-all active:scale-95"
                                style={{ background: 'var(--gold-dark)', color: '#1c1917', border: '1px solid var(--gold-dark)' }}
                            >
                                {t('app.header.connect_wallet_btn')}
                            </button>
                        )}
                        {/* CF-HEADER-HELP-MENU-V1: ⋯ overflow menu (Feedback · Terms · Support).
                            Far-right of the cluster so its dropdown anchors to the screen edge. */}
                        <HeaderMenu onFeedback={() => setScreen('feedback')} onTerms={openTerms} onPrivacy={openPrivacy} onHowItWorks={() => setShowOnboarding(true)} />
                    </div>
                </div>
            </header>

            {/* Wallet Menu */}
            {showWalletMenu && wallet && (
                <div className="fixed inset-0 z-[100] flex items-end justify-center"
                     style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)' }}
                     onClick={() => setShowWalletMenu(false)}>
                    <div className="w-full max-w-lg rounded-t-3xl"
                         style={{ background: 'var(--bg-card)' }}
                         onClick={e => e.stopPropagation()}>
                        <div className="flex justify-center pt-3 pb-4">
                            <div className="w-10 h-1 rounded-full" style={{ background: 'var(--border-subtle)' }} />
                        </div>

                        <div className="px-6 pb-8">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-12 h-12 rounded-2xl flex items-center justify-center"
                                     style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                                    <img src={cfDiamondMark} alt="" className="w-7 h-7" />
                                </div>
                                <div>
                                    <p className="text-[10px] font-medium uppercase tracking-wider"
                                       style={{ color: 'var(--emerald)' }}>{t('app.wallet.onchain_balance_label')}</p>
                                    <p className="text-lg font-black" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                        {(() => { if (!balance) return '...'; const r = parseTonAmount(balance); return r.ok ? r.ton : '...'; })()} <span className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>{t('common.ton_unit')}</span>
                                    </p>
                                </div>
                            </div>

                            <div className="rounded-xl p-3 mb-5"
                                 style={{ background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)' }}>
                                <p className="text-[10px] font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
                                    {t('app.wallet.address_label')}
                                </p>
                                <p className="text-[11px] font-mono break-all leading-relaxed"
                                   style={{ color: 'var(--text-secondary)' }}>
                                    {userFriendlyAddress}
                                </p>
                            </div>

                            {/* In-game money — CF-BALANCE-VISIBILITY-V1 Surface A (Branch B).
                                Cash and bonus chips sit side by side at the SAME size, so the two
                                figures can be added by eye. PL #13670: a player holding 5 cash and
                                5 chips saw only the 5 on top and had to scroll to find the rest.

                                ⛔ NO AGGREGATE, deliberately. The backend funds flips burn-first
                                from sticky (flip-engine.ts -> bonus-engine.ts) while this app's
                                real-mode entry and affordability gates are cash-only (CoinFlip.tsx),
                                so a single "10.00 playable" headline would assert something the app
                                does not honour: it would show 10 while disabling an 8 TON stake, and
                                show 0 cash + 5 chips as playable when that falls into demo. Summing
                                is a behaviour change, not a display change — see Branch A.

                                The chips note below is the product's own sentence about burn-first
                                order. It is what makes two separate figures legible rather than
                                arbitrary: chips are spent first, so they are not interchangeable
                                with cash and must not be shown as one pool. */}
                            {balanceSurfaceView.walletMoneyRowVisible && (
                                <div className="mb-4 pb-3"
                                     style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                    <div className="flex items-start gap-4">
                                        <WalletMoneyCell
                                            label={t('app.wallet.ingame_balance_label')}
                                            tone="cash"
                                            display={balanceSurfaceView.cash}
                                        />
                                        {/* `chipsRowVisible` already implies not-hidden; the second
                                            conjunct is what narrows the union for the type-checker. */}
                                        {balanceSurfaceView.chipsRowVisible && balanceSurfaceView.chips.kind !== 'hidden' && (
                                            <WalletMoneyCell
                                                label={t('bonus.active.chips_label')}
                                                tone="chips"
                                                display={balanceSurfaceView.chips}
                                            />
                                        )}
                                    </div>
                                    {balanceSurfaceView.chips.kind === 'amount' && (
                                        <p className="text-[10px] mt-2 leading-relaxed"
                                           style={{ color: 'var(--text-muted)' }}>
                                            {t('bonus.active.chips_note')}
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Withdraw / status row */}
                            <div className="space-y-2">
                                {hasKnownPositiveBalance && balNano !== null && balanceFresh && (
                                    <button
                                        onClick={() => {
                                            setShowWalletMenu(false);
                                            // Snapshot at deliberate-open instant. Render guard
                                            // is now descriptor-only (not live balance), so
                                            // background polling can't unmount the modal once open.
                                            // CF-WD-CROSS-DEVICE-DEST-V1: dest = BOUND wallet, never the connected one.
                                            const addrSnap = boundWalletAddress;
                                            if (addrSnap) {
                                                setWithdrawDescriptor({ balanceNano: balNano.toString(), address: addrSnap });
                                            }
                                        }}
                                        className="w-full py-3 rounded-2xl text-[13px] font-bold text-center transition-all active:scale-95"
                                        style={{ background: 'rgba(16, 185, 129, 0.08)', color: 'var(--emerald)', border: '1px solid rgba(16, 185, 129, 0.2)' }}
                                    >
                                        {t('app.wallet.withdraw_btn', { amount: formatTonExact(balNano) })}
                                    </button>
                                )}
                                {balanceSurfaceView.walletMenuCashStatus === 'checking' && (
                                    <div
                                        className="w-full py-3 rounded-2xl text-[13px] font-bold text-center"
                                        style={{ background: 'rgba(244, 213, 111, 0.04)', color: 'var(--text-muted)', border: '1px solid rgba(244, 213, 111, 0.10)' }}
                                    >
                                        {t('app.wallet.checking_session')}
                                    </div>
                                )}
                                <div className="flex gap-2">
                                    <button
                                        onClick={handleSwitchClick}
                                        className="flex-1 py-2.5 rounded-2xl text-[11px] font-bold text-center transition-all active:scale-95"
                                        style={{ background: 'rgba(244, 213, 111, 0.06)', color: 'var(--gold)', border: '1px solid rgba(244, 213, 111, 0.15)' }}
                                    >
                                        {t('app.wallet.switch_btn')}
                                    </button>
                                    <button
                                        onClick={handleDisconnectClick}
                                        className="flex-1 py-2.5 rounded-2xl text-[11px] font-bold text-center transition-all active:scale-95"
                                        style={{ background: 'rgba(239, 68, 68, 0.04)', color: 'var(--red)', border: '1px solid rgba(239, 68, 68, 0.1)' }}
                                    >
                                        {t('app.wallet.disconnect_btn')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <main className="flex-1 px-4 pt-2 pb-24 overflow-y-auto">
                    {screen === 'flip' && (<>
                        {/* CF-GROWTH-V1: de-identified wins ticker (social proof) atop the flip
                            screen. Self-hides when there is nothing to show.
                            CF-POT-COUNTER-REMOVE-V1: the bonus-pool scarcity counter that rendered
                            directly below it was removed on PL's instruction (TG #12916). */}
                        <WinsTicker view={ticker} />
                        <CoinFlip
                            wallet={wallet}
                            walletAddress={userFriendlyAddress}
                            rawAddress={rawAddress}
                            connectionRestored={connectionRestored}
                            balance={balance}
                            balanceState={balanceState}
                            depositBalance={depositBalance}
                            maxBetNano={maxBetNano}
                            balanceLoaded={balanceLoaded}
                            balanceFresh={balanceFresh}
                            balanceSurface={balanceSurfaceView}
                            balanceLoadedAtMs={balanceLoadedAtMs}
                            tierConfig={tierConfig}
                            selectedTier={selectedTier}
                            setSelectedTier={setSelectedTier}
                            isTelegram={isTelegram}
                            showDeposit={showDeposit}
                            setShowDeposit={setShowDeposit}
                            showInfo={showInfo}
                            setShowInfo={setShowInfo}
                            onConnect={handleConnectClick}
                            onDisconnect={() => tonConnectUI.disconnect()}
                            apiBase={API_BASE}
                            onVerify={(flipRequestId: string) => {
                                setVerifyFlipId(flipRequestId);
                                setScreen('verify');
                            }}
                            onBalanceRefresh={fetchBalance}
                            bonusView={bonusView}
                            onBonusRefresh={refreshBonus}
                            onDepositRefresh={() => {
                                fetchDepositBalance();
                                // Fires at flip reveal AND on confirmed deposit credit — the
                                // two events that move chips/turnover. NOT inferred from a
                                // balance change: a fully chip-funded flip moves turnover
                                // while cash stays put.
                                refreshBonus('flip_settled');
                                refreshGrowth('flip_settled');
                            }}
                            onOpenWithdraw={() => {
                                // CF-WITHDRAW-BUTTON-REINSTATE-V1: same deliberate-open
                                // snapshot as the wallet-menu green Withdraw button.
                                // App owns withdrawDescriptor; CoinFlip only signals intent.
                                // Guard mirrors the wallet-menu gate (positive known balance
                                // + bound address); no-op otherwise so we never open the
                                // modal on unknown/zero balance.
                                // CF-WD-CROSS-DEVICE-DEST-V1: dest = BOUND wallet, never the connected one.
                                const addrSnap = boundWalletAddress;
                                if (hasKnownPositiveBalance && balNano !== null && addrSnap && balanceFresh) {
                                    setWithdrawDescriptor({ balanceNano: balNano.toString(), address: addrSnap });
                                }
                            }}
                        />
                        {/* ⛔ THE BONUS PANEL IS NOT RENDERED HERE ANY MORE. PL #13704: "I told you
                            to put this on the Activity screen … It's still here?" His #13673 said
                            the panel "has to be shown on the Activity tab view"; I read that as
                            additive and MIRRORED it onto Activity while leaving this copy on Flip.
                            He meant MOVE. The single copy now lives on the Activity tab. */}
                        {/* CF-TERMS-GATE-REMOVE-V1 + CF-PRIVACY-PAGE-V1: tiny footer links — Terms → /terms,
                            Privacy → /privacy (split from the former combined "Terms · Privacy" → /terms now
                            that a dedicated /privacy page exists). Both open in Telegram's in-app browser
                            with a window.open fallback — never a silent no-op. */}
                        <div className="text-center pt-1 pb-3 text-[10px] tracking-wide" style={{ color: 'var(--text-muted)' }}>
                            <button
                                onClick={openTerms}
                                className="underline-offset-2 hover:underline transition-colors active:opacity-70"
                                style={{ color: 'var(--text-muted)' }}
                            >
                                {t('header_menu.terms')}
                            </button>
                            <span aria-hidden="true"> · </span>
                            <button
                                onClick={openPrivacy}
                                className="underline-offset-2 hover:underline transition-colors active:opacity-70"
                                style={{ color: 'var(--text-muted)' }}
                            >
                                {t('header_menu.privacy')}
                            </button>
                        </div>
                        {/* CF-DRAGONTIGER-THEME-V1 Phase B (D3): decorative footer
                            flourish — variant-gated, placed at the page bottom well
                            away from any payout / result / balance. */}
                        {isDragonTiger && (
                            <div className="dt-footer-flourish pb-3">
                                8 <span className="dt-eight">·</span> 88 <span className="dt-eight">·</span> 888 — 招财进宝 · luck &amp; fortune
                            </div>
                        )}
                        {/* CF-GERMAN-VARIANT-V1: sober maker's-mark footer flourish —
                            variant-gated, well away from any payout / balance. */}
                        {isGerman && (
                            <div className="de-footer-flourish pb-3">
                                <span className="de-mark">Kopf oder Zahl</span> · Münzwurf auf TON · Nachweisbar fair
                            </div>
                        )}
                        {/* CF-BRAZIL-VARIANT-V1: sober maker's-mark footer flourish —
                            variant-gated, well away from any payout / balance. */}
                        {isBrazil && (
                            <div className="br-footer-flourish pb-3">
                                <span className="br-mark">Cara ou Coroa</span> · Lançamento de moeda na TON · Comprovadamente justo
                            </div>
                        )}
                    </>)}
                    {screen === 'history' && (
                        <>
                        {/* ⛔ CORRECTED 2026-08-21. This said "MIRROR, not move" and called removing
                            the flip-screen copy "a product call PL has not made." He had made it —
                            #13673 "this has to be shown on the Activity tab view" meant MOVE, and he
                            had to say so twice (#13704) before I heard it. THE SINGLE render site is
                            here. BonusPanel returns null for a server-confirmed `none`, so on an
                            account with no bonus this costs an empty render and nothing else. */}
                        <div className="pb-3">
                            <BonusPanel
                                view={bonusView}
                                bootstrapped={bonusBootstrapped}
                                initialReadExhausted={bonusInitialReadExhausted}
                                termsHref="/bonus-terms"
                            />
                        </div>
                        <History
                            apiBase={API_BASE}
                            onFeedback={() => setScreen('feedback')}
                            onVerify={(flipRequestId: string) => {
                                setVerifyFlipId(flipRequestId);
                                setScreen('verify');
                            }}
                        />
                        </>
                    )}
                    {screen === 'feedback' && (
                        <Feedback apiBase={API_BASE} walletAddress={userFriendlyAddress} onClose={() => setScreen('history')} />
                    )}
                    {screen === 'verify' && <Verify apiBase={API_BASE} initialFlipId={verifyFlipId} />}
                    {screen === 'leaderboard' && <Leaderboard apiBase={API_BASE} />}
            </main>

            {/* Bottom Navigation */}
            <nav className="fixed bottom-0 left-0 right-0 glass pb-safe z-50"
                 style={{ borderTop: '1px solid var(--border-subtle)' }}>
                <div className="flex">
                    {(() => {
                        const allTabs = [
                            { id: 'flip' as Screen, icon: CoinIcon, label: t('app.nav.flip') },
                            { id: 'history' as Screen, icon: ClockIcon, label: t('app.nav.activity') },
                            { id: 'leaderboard' as Screen, icon: TrophyIcon, label: t('app.nav.leaders') },
                            { id: 'verify' as Screen, icon: ShieldIcon, label: t('app.nav.verify') },
                        ];
                        const tabs = wallet ? allTabs : allTabs.filter(t => t.id !== 'verify');
                        return tabs;
                    })().map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setScreen(tab.id)}
                            className="flex-1 flex flex-col items-center gap-0.5 py-2.5 relative transition-colors"
                            style={{ color: screen === tab.id ? 'var(--gold)' : 'var(--text-muted)' }}
                        >
                            {screen === tab.id && (
                                <span className="absolute top-0 left-1/2 -translate-x-1/2 w-5 h-0.5 rounded-b-full"
                                      style={{ background: 'var(--gold)' }} />
                            )}
                            <tab.icon />
                            <span className="text-[10px] font-semibold tracking-wide">{tab.label}</span>
                        </button>
                    ))}
                </div>
            </nav>

            {/* Withdraw Modal — descriptor-driven (CF-FRONTEND-WITHDRAW-STATE-LATCH-FIX-V1).
                Render guard is descriptor-truthiness ONLY. Background balance polling
                no longer unmounts/remounts the modal. Modal closes via deliberate
                Cancel / success / wallet-address-change only. */}
            {withdrawDescriptor && (
                <WithdrawModal
                    apiBase={API_BASE}
                    address={withdrawDescriptor.address}
                    balanceNano={withdrawDescriptor.balanceNano}
                    onClose={() => setWithdrawDescriptor(null)}
                    onSuccess={() => {
                        // Terminal success → parent state-ownership invariant
                        // requires explicit descriptor clear here, not implicitly
                        // via the subsequent onClose() call from WithdrawModal.tsx.
                        // Per Scorer R2 (1035Z): the invariant must be true in
                        // code, not true only because the current child happens
                        // to call onClose after onSuccess. Double-clearing via
                        // onClose is harmless.
                        setWithdrawDescriptor(null);
                        // Drop to loading while we re-validate the new balance
                        // from the authoritative source. Never reuse the prior
                        // value during the refetch window.
                        setBalanceState({ kind: 'loading' });
                        fetchDepositBalance();
                        // D1: withdrawing before wagering completes VOIDS the remaining chips.
                        // The status surface must not keep showing chips the player no longer has.
                        refreshBonus('withdrawal_completed');
                    }}
                />
            )}

            {/* Wallet-switch / disconnect warning. Predicates use custodial loaded-
                balance truth (Binding 8). No legacy channel-status branches. */}
            {showSwitchWarning && (
                <div
                    className="fixed inset-0 z-[110] flex items-center justify-center bg-black/85 backdrop-blur-sm px-6"
                    onClick={handleWarningCancel}
                >
                    <div
                        className="bg-[#1a1a2e] rounded-2xl p-6 w-full max-w-sm shadow-2xl border border-yellow-500/20"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="text-center mb-4">
                            <div className="text-3xl mb-2">⚠️</div>
                            <h3 className="text-lg font-bold text-white">
                                {pendingWalletAction === 'switch' ? t('app.switch_warning.switch_title') : t('app.switch_warning.disconnect_title')}
                            </h3>
                        </div>

                        <div className="text-sm text-slate-400 mb-5 leading-relaxed">
                            {hasKnownPositiveBalance && balNano !== null ? (
                                t('app.switch_warning.has_funds_body', { amount: formatTonExact(balNano) })
                            ) : (
                                t('app.switch_warning.unknown_balance_body')
                            )}
                        </div>

                        {hasKnownPositiveBalance && balNano !== null && balanceFresh && (
                            <button
                                onClick={handleWarningWithdrawFirst}
                                className="w-full py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-bold rounded-xl transition-colors mb-2"
                            >
                                {t('app.switch_warning.withdraw_first_btn')}
                            </button>
                        )}

                        <button
                            onClick={handleWarningContinue}
                            className="w-full py-3 bg-slate-700 hover:bg-slate-600 text-white font-bold rounded-xl transition-colors mb-2"
                        >
                            {pendingWalletAction === 'switch' ? t('app.switch_warning.switch_anyway_btn') : t('app.switch_warning.disconnect_anyway_btn')}
                        </button>

                        <button
                            onClick={handleWarningCancel}
                            className="w-full py-2 text-slate-400 hover:text-white transition-colors text-sm"
                        >
                            {t('common.cancel_btn')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ═══ Nav Icons ═══

function CoinIcon() {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v12M9 9h6M9 15h6" />
        </svg>
    );
}

function ClockIcon() {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
        </svg>
    );
}

function TrophyIcon() {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M8 21h8M12 17V21M7 4h10l1 8H6L7 4z" />
            <circle cx="12" cy="2" r="1" />
        </svg>
    );
}

function ShieldIcon() {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <path d="M9 12l2 2 4-4" />
        </svg>
    );
}
